import { Button, Card, Popconfirm, Table, Tag, message } from 'antd'
import { useFlagRunHistory, useRollbackRun } from '../../../api/flagAdmin'
import type { ColumnsType } from 'antd/es/table'
import moment from 'moment'

const getStatusColor = (status: string) => {
  switch (status) {
    case 'running':
      return 'processing'
    case 'completed':
      return 'success'
    case 'failed':
      return 'error'
    case 'rolled_back':
      return 'warning'
    default:
      return 'default'
  }
}

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'running':
      return 'Kör...'
    case 'completed':
      return 'Klar'
    case 'failed':
      return 'Misslyckades'
    case 'rolled_back':
      return 'Återställd'
    default:
      return status
  }
}

const RunHistory = () => {
  const { data, isLoading } = useFlagRunHistory()
  const rollbackMutation = useRollbackRun()

  const handleRollback = async (runId: number) => {
    try {
      await rollbackMutation.mutateAsync(runId)
      message.success('Körning återställd')
    } catch {
      message.error('Kunde inte återställa körning')
    }
  }

  const columns: ColumnsType<any> = [
    { title: 'Flagga', dataIndex: 'flag_name', key: 'flag_name' },
    {
      title: 'Period',
      key: 'period',
      render: (_, record) => `${moment(record.period_from).format('YYYY-MM')} — ${moment(record.period_to).format('YYYY-MM')}`,
    },
    { title: 'Avvikelsetyp', dataIndex: 'avvikelsetyp', key: 'avvikelsetyp' },
    {
      title: 'Typ',
      dataIndex: 'dry_run',
      key: 'dry_run',
      render: (dryRun: boolean) => (dryRun ? <Tag>Dry-run</Tag> : <Tag color="blue">Sparad</Tag>),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <Tag color={getStatusColor(status)}>{getStatusLabel(status)}</Tag>,
    },
    { title: 'Resultat', dataIndex: 'result_count', key: 'result_count' },
    { title: 'Av', dataIndex: 'created_by', key: 'created_by' },
    {
      title: 'Tidpunkt',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (val: string) => {
        // The flag_run_log.created_at column is TIMESTAMP WITHOUT TIME ZONE.
        // The DB (timezone=Europe/Stockholm) stores local Stockholm time, but
        // TypeORM + pg driver both apply a UTC conversion, resulting in a
        // double-offset (value arrives 2×TZ hours behind actual time).
        // We correct by adding back one browser timezone offset.
        const d = new Date(String(val))
        const corrected = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
        const pad = (n: number) => n.toString().padStart(2, '0')
        return `${corrected.getFullYear()}-${pad(corrected.getMonth() + 1)}-${pad(corrected.getDate())} ${pad(corrected.getHours())}:${pad(corrected.getMinutes())}`
      },
    },
    {
      title: '',
      key: 'actions',
      render: (_, record) => {
        const canRollback = !record.dry_run && record.status === 'completed' && record.flag_instance_ids && record.flag_instance_ids.length > 0
        if (!canRollback) return null
        return (
          <Popconfirm
            title="Är du säker på att du vill återställa denna körning? Alla skapade flaggor kommer att tas bort."
            onConfirm={() => handleRollback(record.id)}
            okText="Återställ"
            cancelText="Avbryt"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger loading={rollbackMutation.isLoading}>
              Återställ
            </Button>
          </Popconfirm>
        )
      },
    },
  ]

  return (
    <Card size="small">
      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={isLoading}
        size="small"
        bordered
        scroll={{ y: 'calc(100vh - 350px)' }}
      />
    </Card>
  )
}

export default RunHistory
